<template>
  <div class="widget-base-input" :data-prefix="true" :data-postfix="true">
    <label
      v-if="label"
      :for="name"
      :data-error="!!error"
      :data-subtitle="!!subtitle"
      :data-active="isInputFocused || !!inputValue"
      class="widget-base-input_label"
      @click="() => input?.focus()"
    >
      {{ label }}
    </label>
    <div
      class="widget-base-input_input relative"
      :class="twMerge(inputVariants({ variant }), String($attrs.class || ''), label ? 'mt-1' : '')"
    >
      <div class="flex w-full items-center gap-x-1.5">
        <WButton
          ref="decreaseBtn"
          type="ghost"
          class="inline-flex size-6 !cursor-pointer items-center justify-center gap-x-2 rounded-custom p-6 text-sm font-medium text-gray-800 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
          tabindex="-1"
          aria-label="Decrease"
          @click="inputValue--"
        >
          <WidgetIcon icon="mdi:minus" height="1.5rem" class="widget-action-icon" />
        </WButton>
        <input
          ref="input"
          v-model="inputValue"
          style="-moz-appearance: textfield; appearance: textfield"
          :placeholder
          class="h-full w-full max-w-20 border-0 !border-none bg-transparent p-0 text-center !shadow-none !outline-none outline-0 !ring-0 focus:ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          :name
          :type
          :autofocus
          :autocomplete
          :readonly
          :data-error="!!error"
          :data-label="!!label"
          v-bind="$attrs"
          @focus="() => (isInputFocused = true)"
          @blur="() => (isInputFocused = false)"
        />
        <WButton
          ref="increaseBtn"
          class="inline-flex size-6 !cursor-pointer items-center justify-center gap-x-2 rounded-custom p-6 text-sm font-medium text-gray-800 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
          tabindex="-1"
          type="ghost"
          aria-label="Increase"
          @click="inputValue++"
        >
          <WidgetIcon :icon="themeIcons.plus" height="1.5rem" class="widget-action-icon" />
        </WButton>
      </div>
    </div>
    <div v-if="error" class="mt-1 grid grid-flow-col justify-start gap-2">
      <WidgetIcon
        v-if="
          !checkTheme('volvo', 'crashdaddy', 'tesla', 'aa', 'suzuki', 'volkswagen', 'bentley', 'vwg', 'samsung', 'audi')
        "
        :icon="themeIcons.closeCircle"
        height="1.25rem"
        width="1.25rem"
        class="mt-0.5 text-negative"
      ></WidgetIcon>
      <p class="font-normal text-negative">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import WidgetIcon from './WidgetIcon.vue';
  import WButton from './Button.vue';
  import { cva, type VariantProps } from 'class-variance-authority';
  import { twMerge } from 'tailwind-merge';
  import { themeIcons, checkTheme, getClientTheme } from 'src/utils/config';
  import { computed, onMounted, ref } from 'vue';
  import { useEventListener } from '@vueuse/core';

  defineOptions({
    inheritAttrs: false,
  });

  const inputVariants = cva(
    'body1 bg-white text-fontPrimary w-full p-3 border border-backgroundSoft-600 rounded-custom block disabled:opacity-50 disabled:cursor-not-allowed focus-within:border-primary focus-within:ring-primary focus-within:ring-offset-0 focus-within:read-only:ring-0 focus-within:shadow-none focus-within:outline-none data-[error=true]:border-negative data-[error=true]:focus-within:ring-negative data-[error=true]:focus-within:border-negative',
    {
      variants: {
        variant: {
          default: 'text-sm focus-within:ring-0',
          audi: 'px-0 pt-0 border-0 border-b border-backgroundSoft focus-within:border-fontPrimary focus-within:ring-none focus-within:ring-0 focus-within:!shadow-[0px_1px_0px_0px] focus-within:shadow-fontPrimary data-[error=true]:focus-within:!shadow-negative',
          samsung:
            'px-0 pt-0 border-0 border-b border-backgroundSoft focus-within:border-fontPrimary focus-within:ring-none focus-within:ring-0 focus-within:!shadow-[0px_1px_0px_0px] focus-within:shadow-fontPrimary data-[error=true]:focus-within:!shadow-negative',
          volkswagen:
            'px-0 pt-0 border-0 border-b border-backgroundSoft focus-within:ring-0 data-[error=true]:focus-within:border-backgroundSoft',
          bentley:
            'px-0 pt-0 border-0 border-b border-backgroundSoft focus-within:ring-0 data-[error=true]:focus-within:border-backgroundSoft',
          vwg: 'px-0 pt-0 border-0 border-b border-backgroundSoft focus-within:ring-0 data-[error=true]:focus-within:border-backgroundSoft',
          mercedes:
            'bg-backgroundSoft text-sm px-4 py-3 focus-within:border-fontPrimary focus-within:ring-0 data-[label=true]:pb-1 data-[label=true]:pt-5',
          tesla:
            'border-none bg-backgroundSoft text-sm p-0 outline-0 focus-within:outline-2 focus-within:outline-2 outline-backgroundSoft-700 focus-within:outline-backgroundSoft-700 focus-within-visible:outline-backgroundSoft-700 transition-all focus-within:ring-none focus-within:ring-0 outline-offset-0 focus-within:outline-offset-0 focus-within-visible:outline-offset-0',
          aa: 'border-none bg-backgroundSoft text-sm p-0 outline-0 focus-within:outline-2 focus-within:outline-2 outline-backgroundSoft-700 focus-within:outline-backgroundSoft-700 focus-within-visible:outline-backgroundSoft-700 transition-all focus-within:ring-none focus-within:ring-0 outline-offset-0 focus-within:outline-offset-0 focus-within-visible:outline-offset-0',
          suzuki:
            'border-none bg-backgroundSoft text-sm p-0 outline-0 focus-within:outline-2 focus-within:outline-2 outline-backgroundSoft-700 focus-within:outline-backgroundSoft-700 focus-within-visible:outline-backgroundSoft-700 transition-all focus-within:ring-none focus-within:ring-0 outline-offset-0 focus-within:outline-offset-0 focus-within-visible:outline-offset-0',
          cinch:
            'border-none bg-backgroundSoft text-sm p-0 outline-0 focus-within:outline-2 focus-within:outline-2 outline-backgroundSoft-700 focus-within:outline-backgroundSoft-700 focus-within-visible:outline-backgroundSoft-700 transition-all focus-within:ring-none focus-within:ring-0 outline-offset-0 focus-within:outline-offset-0 focus-within-visible:outline-offset-0',
          bmw: 'py-3 px-4 focus-within:border-fontPrimary focus-within:ring-fontPrimary',
          toyota: 'py-3 px-4 focus-within:border-fontPrimary focus-within:ring-fontPrimary',
          mopar: 'py-3 px-4',
          jeep: 'py-3 px-4',
          mini: 'py-3 px-4 focus-within:border-fontPrimary focus-within:ring-fontPrimary',
          jlr: 'focus-within:border-fontPrimary focus-within:ring-0 !font-widget-primary',
          common: 'focus-within:border-fontPrimary focus-within:ring-0',
          referral: 'focus-within:border-fontPrimary focus-within:ring-0 !font-widget-primary',
          jaguar: 'p-5 focus-within:border-fontPrimary focus-within:ring-0',
          landrover: 'p-5 focus-within:border-fontPrimary focus-within:ring-0',
          volvo: 'px-5 py-4',
          crashdaddy: 'px-5 py-4',
        },
      },
      defaultVariants: {
        variant: getClientTheme(),
      },
    }
  );

  type InputProps = VariantProps<typeof inputVariants>;

  const props = withDefaults(
    defineProps<{
      modelValue?: number;
      value?: number;
      label?: string;
      placeholder?: string;
      name?: string;
      type?: string;
      autofocus?: boolean;
      autocomplete?: string;
      readonly?: boolean;
      clearable?: boolean;
      error?: string;
      variant?: InputProps['variant'];
      subtitle?: boolean;
      id?: string;
    }>(),
    {
      modelValue: 0,
      value: 0,
      label: '',
      placeholder: '',
      name: '',
      type: 'text',
      autofocus: false,
      autocomplete: undefined,
      readonly: false,
      clearable: false,
      subtitle: false,
      error: '',
      variant: undefined,
      id: '',
    }
  );

  const emit = defineEmits<{
    (event: 'update:modelValue', value: number): void;
    (event: 'clear'): void;
  }>();

  const input = ref<HTMLInputElement | null>(null);
  const inputValue = computed({
    get: () => props.modelValue ?? props.value,
    set: (val) => {
      emit('update:modelValue', val);
    },
  });
  const error = computed(() => props.error);
  const isInputFocused = ref(false);

  const increaseBtn = ref<HTMLButtonElement | null>(null);
  const decreaseBtn = ref<HTMLButtonElement | null>(null);

  onMounted(() => {
    let decreaseInterval: NodeJS.Timeout;
    let increaseInterval: NodeJS.Timeout;

    useEventListener(decreaseBtn, 'pointerdown', () => {
      decreaseInterval = setInterval(() => {
        inputValue.value -= 1;
      }, 300);
    });
    useEventListener(increaseBtn, 'pointerdown', () => {
      increaseInterval = setInterval(() => {
        inputValue.value += 1;
      }, 300);
    });

    useEventListener(decreaseBtn, 'pointerup', () => {
      clearInterval(decreaseInterval);
    });
    useEventListener(increaseBtn, 'pointerup', () => {
      clearInterval(increaseInterval);
    });
  });
</script>
